// Angular
import { Injectable } from '@angular/core';

// Third party
import { Observable } from 'rxjs';

// Local library
import {
  ICommuicationCallResponse,
  IGetAllEventsNameAndIdResponse,
  IGetCompressedCallResponse,
  IGetEventDetailsCallResponse,
  IGetEventsDetailsResponse,
  IGetRunnerResponse,
  IResponseModel,
  IToteProgramCallResponse
} from '@amwager/util-ts';

// Local file
import { HttpService } from '../services';



// TODO: write tests
@Injectable()
export class ToteProgramAPIService {
  private readonly API_URL = '/api/ToteProgram';

  constructor(
    private readonly httpService: HttpService,
  ) {}


  /**
   * GET API
   */

  /**
   * @description
   * Get all runner starting with a specific string.
   *
   * @param runnerPrefix string
   * @returns Observable<IResponseModel<IGetRunnerResponse>>
   */
  geRunners$(runnerPrefix: string): Observable<IResponseModel<IGetRunnerResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GeRunners`,
      params: this.httpService.objToHttpParams({
        runnerPrefix
      })
    });
  }

  /**
   * @description
   * Get all changes reported by Equibase.
   *
   * @param equibaseEventId string
   * @param raceDate string
   * @returns Observable<IResponseModel<{}>>
   */
  getEquibaseChanges$(equibaseEventId: string, raceDate?: string)
    // eslint-disable-next-line @typescript-eslint/ban-types
    : Observable<IResponseModel<{}>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEquibaseChanges`,
      params: this.httpService.objToHttpParams({
        equibaseEventId,
        raceDate
      })
    });
  }

  /**
   * @description
   * Get all current relevant events to wager on.
   * Does not include race/runner/pool data.
   *
   * @returns Observable<IResponseModel<IToteProgramCallResponse>>
   */
  getAllEvents$(): Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEvents`
    });
  }

  /**
   * @description
   * Get of all events from a specific date.
   * Does not include race/runner/pool data.
   *
   * @param requestedDateStr string
   * @returns Observable<IResponseModel<IToteProgramCallResponse>>
   */
  getAllEventsByDate$(requestedDateStr: string)
    : Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEventsByDate`,
      params: this.httpService.objToHttpParams({
        requestedDateStr
      })
    });
  }

  /**
   * @description
   * Get of all events from a specific date.
   * Does not include race/runner/pool data.
   *
   * @param requestedDateStr string
   * @returns Observable<IResponseModel<IToteProgramCallResponse>>
   */
  getAllEventsByDateForAdmin$(requestedDateStr: string)
    : Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEventsByDateForAdmin`,
      params: this.httpService.objToHttpParams({
        requestedDateStr
      })
    });
  }

  /**
   * @description
   * Get a list of all tracks known by the system.
   *
   * @returns Observable<IResponseModel<{}>>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getAllTracksForGeo$(): Observable<IResponseModel<{}>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllTracksForGeo`,
    });
  }

  /**
   * @description
   * Gets event, race and pool data trimmed down for non-wagering purposes.
   * (Do not use unless advised to do so as it is missing data for most uses)
   *
   * @param eventId string
   * @returns Observable<IResponseModel<{}>>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getEventDetailLite$(eventId: string): Observable<IResponseModel<{}>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventDetailLite`,
      params: this.httpService.objToHttpParams({
        eventId
      })
    });
  }

  /**
   * @description
   * Gets a list of all event names and ids the system has ever seen.
   *
   * @returns Observable<IResponseModel<IGetAllEventsNameAndIdResponse>>
   */
  getAllEventsNameAndId$(): Observable<IResponseModel<IGetAllEventsNameAndIdResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEventsNameAndId`,
    });
  }

  /**
   * @description
   * Get an object representing event details for all of todays events. Includes
   * ALL child objects. (Races, Runners, Pools, Morning Line Data, Etc..)
   *
   * @param runId string
   * @returns Observable<IResponseModel<IGetEventsDetailsResponse[]>>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getEventsDetails$(runId?: string): Observable<IResponseModel<IGetEventsDetailsResponse[]>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventsDetails`,
      params: this.httpService.objToHttpParams({
        runId,
        callOrigin: 0
      })
    });
  }

  /**
   * @description
   * Get a compressed object representing event details for all of todays events.
   * Includes ALL child objects. (Races, Runners, Pools, Morning Line Data, Etc..)
   *
   * @param callOrigin: 0|1|2
   * @param runId string
   * @returns Observable<IResponseModel<IGetCompressedCallResponse>>
   */
  getCompressedEventsDetails$(callOrigin: 0|1|2= 0, runId?: string)
    : Observable<IResponseModel<IGetCompressedCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetCompressedEventsDetails`,
      params: this.httpService.objToHttpParams({
        callOrigin,
        runId
      })
    });
  }

  /**
   * @description
   * Get the details (Races/Runners/Pools/Etc..) of a specific event
   *
   * @param eventId string
   * @param runId string
   * @param callOrigin 0|1|2
   * @returns Observable<IResponseModel<IGetEventDetailsCallResponse>>
   */
  getEventDetail$(eventId: string, runId: string, callOrigin: 0|1|2= 0)
    : Observable<IResponseModel<IGetEventDetailsCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventDetail`,
      params: this.httpService.objToHttpParams({
        eventId,
        runId,
        callOrigin
      })
    });
  }

  /**
   * @description
   * Get the details (Races/Runners/Pools/Etc..) of a specific event (Admin use)
   *
   * @param eventId string
   * @param runId string
   * @param callOrigin 0|1|2
   * @returns Observable<IResponseModel<IGetEventDetailsCallResponse>>
   */
  getEventDetailForAdmin$(eventId: string, runId: string, callOrigin: 0|1|2= 0)
    : Observable<IResponseModel<IGetEventDetailsCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventDetailForAdmin`,
      params: this.httpService.objToHttpParams({
        eventId,
        runId,
        callOrigin
      })
    });
  }

  /**
   * @description
   * Get the current status of the system.
   *
   * @returns Observable<IResponseModel<ICommuicationCallResponse>>
   */
  getState$(): Observable<IResponseModel<ICommuicationCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetState`,
    });
  }

  /**
   * @description
   * Get detailed information for a specific date. Usefull for updates.
   * (Includes Runners, Pools, Etc..)
   *
   * @param raceId number
   * @param runId string
   * @param eventId string
   * @returns Observable<IResponseModel<IToteProgramCallResponse>>
   */
  getRace$(raceId: number, runId: string, eventId: string)
    : Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetRace`,
      params: this.httpService.objToHttpParams({
        raceId,
        runId,
        eventId
      })
    });
  }

}
